import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { MatTabNavPanel } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { OperationNavigationService } from '@data-terminal/data-access/navigation/operation-navigation.service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { isLightModeMachine } from '@data-terminal/shared-models/machine';
import { NavLink, ROUTE_PARAMS } from '@data-terminal/shared-models/navigation';
import { ActivityRequired } from '@data-terminal/shared-models/operation';
import { OperationNavConfig } from '@data-terminal/shared-models/operation-navigation';
import { getParam } from '@data-terminal/utils/helpers';
import { combineLatest, map, Observable, ReplaySubject } from 'rxjs';

@Component({
    selector: 'app-operation-navigation-tab',
    templateUrl: './operation-navigation-tab.component.html',
    styleUrls: ['./operation-navigation-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class OperationNavigationTabComponent {
    private readonly operationNavigation = inject(OperationNavigationService);
    private readonly userMachinesService = inject(UserMachinesService);
    private readonly route = inject(ActivatedRoute);
    private readonly links$ = new ReplaySubject<NavLink[]>(1);

    @Input() tabPanel!: MatTabNavPanel;
    @Input() set links(links: NavLink[] | undefined) {
        this.links$.next(links ?? []);
    }
    @Input() isSecondLevel!: boolean;

    public operationTabsConfig$: Observable<OperationNavConfig>;
    public visibleLinks$: Observable<NavLink[]>;

    constructor() {
        this.operationTabsConfig$ = this.operationNavigation.getOperationTabsConfig$();
        this.visibleLinks$ = combineLatest([this.userMachinesService.getUserMachines$(), this.links$]).pipe(
            map(([userMachines, links]) => {
                const machineId = getParam(this.route, ROUTE_PARAMS.machineId);
                const machine = userMachines.allMachines.find((item) => item.machineId === machineId);
                if (machine && isLightModeMachine(machine)) {
                    return links.filter((link) => link.id !== ActivityRequired.OTHER_ACTIVITY);
                }
                return links;
            })
        );
    }

    public isLinkDisabled(link: NavLink, config: OperationNavConfig): boolean {
        return !!(link.id && config[link.id].isDisabled);
    }
}
